// native
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

// service
import { DevicesService } from 'app/core/services/devices.service';
import { ExportTaskService } from 'app/core/services/export-task.service';
import { PreferencesService } from 'app/core/services/preferences.service';
import { ErrorService } from 'app/core/services/error.service';
import { UtilityService } from 'app/core/services/utility.service';

// models
import { CenterContainerTitle, ExportAcuityTaskParameters, ExportFunctionalVisionTaskParameters, ExportPerimetryTaskParameters, ExportTaskCreateBody, TestProtocol, TestStrategy } from '../models';
import { EXPORT_TASK_TYPES } from 'app/constants';

@Component({
    selector: 'app-export-task-form',
    templateUrl: './export-task-form.component.html',
    standalone: false
})
export class ExportTaskFormComponent implements OnInit {
  form: UntypedFormGroup;
  title: CenterContainerTitle = {};

  exportTaskTypes: { name: string; value: string; }[] = [];
  displayedTestStrategies: TestStrategy[] = [];
  displayedTestProtocols: TestProtocol[] = [];
  deviceTypes: { name: string; value: string; }[] = [];
  
  isLoading: boolean = false;

  constructor(
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private devicesService: DevicesService,
    private exportTaskService: ExportTaskService,
    public preferencesService: PreferencesService,
    private translateService: TranslateService,
    private errorService: ErrorService,
    private utilityService: UtilityService,
    private toastService: ToastrService
  ) { }

  ngOnInit() {
    this.exportTaskTypes = this.exportTaskService.getExportTaskTypes();
    this.displayedTestStrategies = this.exportTaskService.getStrategies();
    this.deviceTypes = this.devicesService.getDeviceTypesForExportTaskCreation();
    this.translateService.get('exportTask').subscribe(() => this.setTranslations());
    this.initializeForm();
  }

  private initializeForm() {
    this.form = this.formBuilder.group({
      export_task_type: ['', Validators.compose([Validators.required])],
      test_strategy: [''],
      test_protocol: [''],
      device_type: [null],
      start_date: [null],
      end_date: [null],
      include_deviations: [false],
      include_reports: [false],
      phase2_only: [false]
    }, {
      validator: this.checkIfMatchingDates(
        'start_date',
        'end_date'
      )
    });

    // Subscribe to export_task_type changes to update validation
    this.form.get('export_task_type').valueChanges.subscribe((exportTaskType) => {
      this.updateValidationForExportTaskType(exportTaskType);
    });

    this.form.get('test_strategy').valueChanges.subscribe(() => {
      const selectedStrategyValue = this.form.controls['test_strategy'].value;
      this.displayedTestProtocols = this.displayedTestStrategies.find(strategy => strategy.value === selectedStrategyValue)?.protocols || [];
    });
  }

  private updateValidationForExportTaskType(exportTaskType: string) {
    const testStrategyControl = this.form.get('test_strategy');
    const testProtocolControl = this.form.get('test_protocol');

    if (exportTaskType === EXPORT_TASK_TYPES.PERIMETRY) {
      // Add required validation for perimetry fields
      testStrategyControl.setValidators([Validators.required]);
      testProtocolControl.setValidators([Validators.required]);
    } else {
      // Remove validation for non-perimetry tasks
      testStrategyControl.clearValidators();
      testProtocolControl.clearValidators();
    }

    // Update validation status
    testStrategyControl.updateValueAndValidity();
    testProtocolControl.updateValueAndValidity();
  }

  private checkIfMatchingDates(fromKey: string, toKey: string): (group: UntypedFormGroup) => void {
    return (group: UntypedFormGroup) => {
      const fromDate = group.controls[fromKey];
      const toDate = group.controls[toKey];

      if (!fromDate?.value || !toDate?.value)
        return toDate.setErrors(null);
      else if (toDate.value < fromDate.value)
        return toDate.setErrors({ invalidDateRange: true });
      else
        return toDate.setErrors(null);
    };
  }

  private setTranslations() {
    this.title.bold = this.translateService.instant('exportTask');
    this.title.thin = this.translateService.instant('new');
  }

  onSubmit(form: UntypedFormGroup) {
    if (this.isLoading)
      return;

    if (!form.valid) {
      form.markAllAsTouched();
      this.toastService.error(this.translateService.instant('formIncompleteMessage'));
      return;
    }

    const {
      export_task_type,
      test_strategy,
      test_protocol,
      device_type,
      start_date,
      end_date,
      include_deviations,
      include_reports,
      phase2_only
    } = this.form.value;

    let parameters: ExportPerimetryTaskParameters | ExportAcuityTaskParameters | ExportFunctionalVisionTaskParameters;
    const startDate = start_date ? this.utilityService.convertClientDateToServerDate(start_date) : null;
    const endDate = end_date ? this.utilityService.convertClientDateToServerDate(end_date) : null;
    
    if (export_task_type === EXPORT_TASK_TYPES.ACUITY) {
      parameters = {
        start_date: startDate,
        end_date: endDate,
        include_reports
      };
    }
    else if (export_task_type === EXPORT_TASK_TYPES.FUNCTIONAL_VISION) {
      parameters = {
        start_date: startDate,
        end_date: endDate,
        include_reports,
        phase2_only
      };
    }
    else {
      parameters = {
        test_strategy,
        test_protocol,
        device_type: device_type || null,
        start_date: startDate,
        end_date: endDate,
        include_deviations,
        include_reports
      };
    }

    const body: ExportTaskCreateBody = {
      type: export_task_type,
      parameters,
    };

    this.isLoading = true;
    this.exportTaskService.create(body).pipe(
      finalize(() => this.isLoading = false)
    ).subscribe(
      response => this.onClose(),
      error => this.errorService.handleError(error)
    );
  }

  onClose() {
    this.router.navigate(['export-tasks']);
  }
}
